import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CircleCheck,
  Eye,
  ExternalLink,
  IndianRupee,
  Inbox,
  Link2,
  Pencil,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Tag,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import CustomPaginator from '../../components/GlobalComponents/CustomPaginator';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import SheetImportsDocs from './parts/SheetImportsDocs';
import ConfirmDeleteModal from './parts/ConfirmDeleteModal';
import ResyncModal, { ResyncPhase, ResyncResult } from './parts/ResyncModal';
import SheetRenameModal from './parts/SheetRenameModal';
import { channelLabel } from './parts/leadOrigin';
import { SyncSpend, fmtINR, spendApi } from '../SpentReports/parts/spendTypes';
import SheetImportDetailsModal, {
  Banner,
  LeadsTile,
  QualityBar,
  SheetGlyph,
  SheetImport,
  SheetTile,
  StatTile,
  StatusPill,
  TONES,
  Viewer,
  fmtDateTime,
  pillStyle,
  qualityPct,
  sheetIdOf,
  sheetLabel,
  shortId,
  userLabel,
} from './parts/SheetImportDetailsModal';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

const SELECT_ARROW = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5'%3E%3Cpath d='M0 0l4.5 5L9 0z' fill='rgba(17,85,80,0.45)'/%3E%3C/svg%3E")`;

// ── Types (API responses) ─────────────────────────────────────────────────────
interface ImportResponse {
  success: boolean;
  message: string;
  import_id: string;
  total_rows: number;
  imported_count: number;
  skipped_count: number;
  failed_count: number;
}
interface ListResponse {
  success: boolean;
  data: SheetImport[];
  pagination: { total: number; page: number; limit: number; pages: number };
  viewer?: Viewer;
}

const SHEET_URL_RE = /\/spreadsheets\/d\/[a-zA-Z0-9-_]+/;
const EMPTY_PAGE = { total: 0, page: 1, limit: 10, pages: 0 };
const RESULT_AUTO_HIDE_MS = 10000;

const SYNC_STEPS = ['Connecting to Google Sheets', 'Reading rows', 'Mapping columns', 'Validating data'];

const FEATURES = ['Auto column mapping', 'Row-level validation', 'Review before push', 'Full sync history'];

const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
const avc = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};

// ─────────────────────────────────────────────────────────────────────────────

const SheetImports: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);

  // Header tab (segmented control)
  const [tab, setTab] = useState<'sync' | 'docs'>('sync');

  // Sync form (a beacon's "Sync now" passes the sheet link in route state)
  const [sheetUrl, setSheetUrl] = useState<string>((location.state as { sheetUrl?: string } | null)?.sheetUrl ?? '');
  const [displayName, setDisplayName] = useState('');

  // Rename (one name per sheet — applied to all its syncs)
  const [renameTarget, setRenameTarget] = useState<SheetImport | null>(null);
  const [importing, setImporting] = useState(false);
  const [syncStep, setSyncStep] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ImportResponse | null>(null);

  // History list
  const [imports, setImports] = useState<SheetImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(EMPTY_PAGE);

  // Spend per sync — admins only (viewer.can_manage_spend)
  const [canSpend, setCanSpend] = useState(false);
  const [spends, setSpends] = useState<Record<string, SyncSpend>>({});

  // Details modal
  const [detailId, setDetailId] = useState<string | null>(null);

  // Delete
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SheetImport | null>(null);

  // Re-sync (only for imports not yet pushed to leads)
  const [resyncingId, setResyncingId] = useState<string | null>(null);
  const [resyncTarget, setResyncTarget] = useState<SheetImport | null>(null);
  const [resyncPhase, setResyncPhase] = useState<ResyncPhase>('confirm');
  const [resyncResult, setResyncResult] = useState<ResyncResult | null>(null);
  const [resyncError, setResyncError] = useState<string | null>(null);
  const syncing = importing || !!resyncingId;

  const fetchImports = useCallback(async () => {
    try {
      setLoading(true);
      setListError(null);
      const p = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter) p.set('status', statusFilter);
      const res = await apiClient.get<ListResponse>(`/v1/sheet-imports?${p}`);
      if (res.data.success) {
        const list = res.data.data ?? [];
        const admin = !!res.data.viewer?.can_manage_spend;
        setImports(list);
        setPagination(res.data.pagination ?? EMPTY_PAGE);
        setCanSpend(admin);
        const pushed = list.filter((x) => x.is_pushed_to_leads).map((x) => x.id);
        if (admin && pushed.length) {
          spendApi.list(pushed).then(setSpends).catch(() => setSpends({}));
        } else {
          setSpends({});
        }
      } else {
        setListError('Failed to load sync history');
      }
    } catch (e: any) {
      setListError(e.response?.data?.error || 'Failed to load sync history');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  // Walk through the sync steps while the request is in flight
  useEffect(() => {
    if (!importing) return;
    setSyncStep(0);
    const t = setInterval(() => setSyncStep((s) => Math.min(s + 1, SYNC_STEPS.length - 1)), 700);
    return () => clearInterval(t);
  }, [importing]);

  // Auto-hide the sync result card after 10s
  useEffect(() => {
    if (!lastResult) return;
    const t = setTimeout(() => setLastResult(null), RESULT_AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [lastResult]);

  const refreshFromTop = () => {
    if (page === 1) fetchImports();
    else setPage(1);
  };

  const handleImport = async () => {
    const url = sheetUrl.trim();
    if (!url) {
      setImportError('Paste a Google Sheets link first.');
      return;
    }
    if (!SHEET_URL_RE.test(url)) {
      setImportError("That doesn't look like a Google Sheets link. It should contain /spreadsheets/d/…");
      return;
    }

    setImporting(true);
    setImportError(null);
    setLastResult(null);
    try {
      const body: { sheet_url: string; display_name?: string } = { sheet_url: url };
      if (displayName.trim()) body.display_name = displayName.trim();
      const res = await apiClient.post<ImportResponse>('/v1/sheet-imports', body);
      setLastResult(res.data);
      setSheetUrl('');
      setDisplayName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Sheet synced',
        detail: `${res.data.total_rows} rows read · ${res.data.imported_count} ready`,
        life: 3000,
      });
    } catch (e: any) {
      setImportError(e.response?.data?.error || 'Sync failed. Please try again.');
    } finally {
      setImporting(false);
      // The backend records failed syncs too, so refresh either way
      refreshFromTop();
    }
  };

  // Clicking the icon only opens the confirmation modal
  const handleResync = (r: SheetImport) => {
    if (resyncingId || importing) return;
    setResyncTarget(r);
    setResyncPhase('confirm');
    setResyncResult(null);
    setResyncError(null);
  };

  const runResync = async () => {
    if (!resyncTarget) return;
    const r = resyncTarget;
    setResyncingId(r.id);
    setResyncPhase('running');
    setResyncError(null);
    try {
      const res = await apiClient.post<ImportResponse>(`/v1/sheet-imports/${r.id}/resync`);
      setResyncResult(res.data);
      setResyncPhase('done');
    } catch (e: any) {
      setResyncError(e.response?.data?.error || 'Could not re-sync this sheet.');
      setResyncPhase('error');
    } finally {
      setResyncingId(null);
      fetchImports();
    }
  };

  const closeResync = () => {
    if (resyncPhase === 'running') return;
    setResyncTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const r = deleteTarget;
    setDeletingId(r.id);
    try {
      await apiClient.delete(`/v1/sheet-imports/${r.id}`);
      toast.current?.show({ severity: 'success', summary: 'Sync deleted', life: 2500 });
      setDeleteTarget(null);
      if (detailId === r.id) setDetailId(null);
      if (lastResult?.import_id === r.id) setLastResult(null);
      // Deleted the last row on this page → step back one page
      if (imports.length === 1 && page > 1) setPage(page - 1);
      else fetchImports();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Delete failed',
        detail: e.response?.data?.error || 'Could not delete this sync.',
        life: 4000,
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Column styles ─────────────────────────────────────────────────────────
  const CH: React.CSSProperties = { whiteSpace: 'nowrap' };

  // ── Templates ─────────────────────────────────────────────────────────────
  const sheetTpl = (r: SheetImport) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px' }}>
      <SheetTile size={32} />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4.6px' }}>
          <a
            href={r.sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            title={r.sheet_url}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3.7px',
              color: C.teal,
              fontSize: '0.72rem',
              fontWeight: 700,
              fontFamily: r.display_name ? undefined : 'monospace',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sheetLabel(r)} <ExternalLink size={10} style={{ flexShrink: 0 }} />
          </a>
          <button
            onClick={() => setRenameTarget(r)}
            title={r.display_name ? 'Rename sheet' : 'Name this sheet'}
            aria-label="Rename sheet"
            className="shi-btn-ghost"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              border: 'none',
              background: 'transparent',
              color: C.muted,
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.2s',
            }}
          >
            <Pencil size={11} />
          </button>
        </div>
        <span style={{ fontSize: '0.63rem', color: C.muted, whiteSpace: 'nowrap' }}>
          {r.display_name && <span style={{ fontFamily: 'monospace' }}>{shortId(sheetIdOf(r))} · </span>}
          {r.detected_channel && <span>{channelLabel(r.detected_channel)} · </span>}
          {r.headers?.length ? `${r.total_rows} rows · ${r.headers.length} columns` : 'No data read'}
        </span>
      </div>
    </div>
  );

  const qualityTpl = (r: SheetImport) =>
    r.status === 'completed' && r.total_rows > 0 ? (
      <div style={{ width: '130px' }}>
        <QualityBar valid={r.imported_count} invalid={r.failed_count} />
      </div>
    ) : (
      <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>
    );

  const pushTpl = (r: SheetImport) => {
    if (r.is_pushed_to_leads) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.8px', alignItems: 'flex-start' }}>
          <span style={pillStyle(TONES.green)}>
            <CircleCheck size={10} /> {r.imported_count} in Leads
          </span>
          <span style={{ fontSize: '0.6rem', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDateTime(r.pushed_at)}</span>
        </div>
      );
    }
    if (r.status === 'completed' && r.imported_count > 0) {
      return <span style={pillStyle(TONES.amber)}>Awaiting review</span>;
    }
    return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
  };

  const spendTpl = (r: SheetImport) => {
    if (!r.is_pushed_to_leads) return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
    const s = spends[r.id];
    if (!s) {
      return (
        <button
          type="button"
          onClick={() => setDetailId(r.id)}
          title="Add what you spent on these leads"
          style={{ ...pillStyle(TONES.amber), cursor: 'pointer' }}
        >
          <IndianRupee size={9} /> Add amount
        </button>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, whiteSpace: 'nowrap' }}>{fmtINR(s.amount_incl_gst_paise)}</span>
        <span style={{ fontSize: '0.6rem', color: '#15803d', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {s.leads_count ? `${fmtINR(s.cost_per_lead_incl_gst)} / lead` : 'no leads'}
        </span>
      </div>
    );
  };

  const byTpl = (r: SheetImport) => {
    const name = userLabel(r.imported_by);
    if (name === '—') return <span style={{ color: C.muted, fontSize: '0.7rem' }}>—</span>;
    const c = avc(name);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5.5px' }}>
        <div
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: c,
            color: '#fff',
            fontSize: '0.55rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {name.substring(0, 2).toUpperCase()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ fontSize: '0.6rem', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</span>
        </div>
      </div>
    );
  };

  const actionTpl = (r: SheetImport) => {
    const disabled = r.status !== 'completed';
    const needsPush = !disabled && !r.is_pushed_to_leads && r.imported_count > 0;
    const viewBtn = (
      <button
        onClick={() => setDetailId(r.id)}
        disabled={disabled}
        title={disabled ? 'This sync did not complete, so there are no rows' : 'Open sync'}
        className={disabled ? undefined : needsPush ? 'shi-btn-lime' : 'shi-btn-ghost'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4.6px',
          padding: '5px 10.1px',
          borderRadius: '6.5px',
          fontSize: '0.65rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          transition: 'all 0.2s',
          background: needsPush ? C.lime : G.shellBg,
          color: needsPush ? C.teal : C.sub,
          border: needsPush ? 'none' : `1px solid ${G.line}`,
        }}
      >
        {needsPush ? <Send size={11} /> : <Eye size={11} />}
        {needsPush ? 'REVIEW & PUSH' : 'VIEW'}
      </button>
    );
    const busy = deletingId === r.id;
    const delBtn = (
      <button
        onClick={() => setDeleteTarget(r)}
        disabled={busy || r.status === 'processing'}
        title={r.status === 'processing' ? 'Wait for the sync to finish' : 'Delete sync'}
        className="shi-btn-danger"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '27px',
          height: '27px',
          borderRadius: '6.5px',
          background: G.shellBg,
          border: `1px solid ${G.line}`,
          color: '#c62828',
          cursor: busy || r.status === 'processing' ? 'not-allowed' : 'pointer',
          opacity: busy || r.status === 'processing' ? 0.45 : 1,
          transition: 'all 0.2s',
        }}
      >
        {busy ? <RefreshCw size={11} className="shi-spinner" /> : <Trash2 size={12} />}
      </button>
    );
    // Re-sync is only offered while the import hasn't been pushed to leads
    const canResync = !r.is_pushed_to_leads && r.status !== 'processing';
    const resyncBusy = resyncingId === r.id;
    const resyncBtn = canResync ? (
      <button
        onClick={() => handleResync(r)}
        disabled={syncing}
        title="Re-sync from Google Sheet"
        className="shi-btn-ghost"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '27px',
          height: '27px',
          borderRadius: '6.5px',
          background: G.shellBg,
          border: `1px solid ${G.line}`,
          color: C.teal,
          cursor: syncing ? 'not-allowed' : 'pointer',
          opacity: syncing && !resyncBusy ? 0.45 : 1,
          transition: 'all 0.2s',
        }}
      >
        <RefreshCw size={12} className={resyncBusy ? 'shi-spinner' : undefined} />
      </button>
    ) : null;
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        {viewBtn}
        {resyncBtn}
        {delBtn}
      </div>
    );
  };

  const emptyState = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '36px 0',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'rgba(22,163,74,0.07)',
          border: '1px solid rgba(22,163,74,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Inbox size={22} color="#15803d" />
      </div>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: C.teal }}>No syncs yet</span>
      <span style={{ fontSize: '0.7rem', color: C.muted }}>Paste a Google Sheets link above to run your first sync.</span>
    </div>
  );

  const sectionTitle = (text: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
      <div
        style={{
          width: '3px',
          height: '14.8px',
          background: `linear-gradient(180deg,${C.lime},#8aaa00)`,
          borderRadius: '3px',
        }}
      />
      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>{text}</span>
    </div>
  );

  const flowNode = (tile: React.ReactNode, label: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6.5px' }}>
      {tile}
      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </div>
  );

  // Segmented control: Sync | Documentation
  const segmented = (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        alignSelf: 'flex-start',
        gap: '2px',
        padding: '3px',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.16)',
      }}
    >
      {(
        [
          ['sync', 'Sync', RefreshCw],
          ['docs', 'Documentation', BookOpen],
        ] as const
      ).map(([k, label, Icon]) => {
        const on = tab === k;
        return (
          <button
            key={k}
            role="tab"
            aria-selected={on}
            onClick={() => setTab(k)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: 700,
              transition: 'all 0.2s',
              background: on ? C.lime : 'transparent',
              color: on ? C.teal : 'rgba(255,255,255,0.75)',
              boxShadow: on ? '0 2px 10px rgba(200,233,75,0.35)' : 'none',
            }}
          >
            <Icon size={12} /> {label}
          </button>
        );
      })}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{`
        .shi-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.64rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:10px 13px !important; }
        .shi-dt .p-datatable-tbody > tr { transition: background 0.15s ease; }
        .shi-dt .p-datatable-tbody > tr:hover { background: rgba(17,85,80,0.03) !important; }
        .shi-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:10px 13px !important; }
        .shi-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
        .shi-dt .p-datatable-emptymessage > td { padding:0 !important; text-align:center !important; border-bottom:none !important; }
        .shi-url input::placeholder { color: rgba(17,85,80,0.4); }
        .shi-url:focus-within { box-shadow: 0 0 0 3px rgba(200,233,75,0.45) !important; }
        .shi-btn-lime:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(200,233,75,0.5) !important; }
        .shi-btn-ghost:hover { background: rgba(17,85,80,0.08) !important; color: #115550 !important; }
        .shi-btn-danger:hover:not(:disabled) { background: rgba(198,40,40,0.1) !important; border-color: rgba(198,40,40,0.35) !important; }
        .shi-btn-glass:hover { background: rgba(255,255,255,0.16) !important; }
        .shi-spinner { animation: shiSpin 0.9s linear infinite; }
        @keyframes shiSpin { to { transform: rotate(360deg); } }
        .shi-sel:focus { outline: none; }
        .shi-flow-dot { position:absolute; top:50%; margin-top:-4px; width:8px; height:8px; border-radius:50%; background:${C.lime}; box-shadow:0 0 10px rgba(200,233,75,0.9); }
        .shi-flow-dot.a { left:0; }
        .shi-flow-dot.b { left:calc(100% - 8px); }
        .shi-flow-dot.c, .shi-flow-dot.d { display:none; }
        /* syncing: dots stream left → right at a constant speed, evenly spaced, like flowing water */
        .shi-flow-run .shi-flow-dot { left:0; animation: shiFlow 1.6s linear infinite; }
        .shi-flow-run .shi-flow-dot.c, .shi-flow-run .shi-flow-dot.d { display:block; }
        .shi-flow-run .shi-flow-dot.a { animation-delay: 0s; }
        .shi-flow-run .shi-flow-dot.b { animation-delay: -0.4s; }
        .shi-flow-run .shi-flow-dot.c { animation-delay: -0.8s; }
        .shi-flow-run .shi-flow-dot.d { animation-delay: -1.2s; }
        @keyframes shiFlow { 0% { transform: translateX(0); opacity: 0; } 12% { opacity: 1; } 88% { opacity: 1; } 100% { transform: translateX(88px); opacity: 0; } }
        .shi-live { animation: shiPulse 1.8s ease-out infinite; }
        @keyframes shiPulse { 0% { box-shadow:0 0 0 0 rgba(200,233,75,0.7); } 100% { box-shadow:0 0 0 7px rgba(200,233,75,0); } }
        @media (max-width: 600px) { .shi-sync-btn { flex: 1 1 100%; } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {/* ── Hero: integration card ──────────────────────────────────── */}
        <div style={SHELL}>
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '9.2px',
              padding: '18.4px 20px 20px',
              background: 'linear-gradient(135deg, #0c403c 0%, #115550 50%, #16706a 100%)',
              color: 'white',
            }}
          >
            {/* spreadsheet-cell grid + lime glow */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                backgroundSize: '30px 22px',
                WebkitMaskImage: 'linear-gradient(115deg, transparent 20%, #000 75%)',
                maskImage: 'linear-gradient(115deg, transparent 20%, #000 75%)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 88% 18%, rgba(200,233,75,0.22), transparent 42%)',
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '16.6px' }}>
              {/* Top bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9.2px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5.5px',
                      padding: '3.7px 10.1px',
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Sparkles size={11} color={C.lime} /> Integration
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '3.7px 10.1px',
                      borderRadius: '20px',
                      background: 'rgba(200,233,75,0.14)',
                      border: '1px solid rgba(200,233,75,0.4)',
                      color: C.lime,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                    }}
                  >
                    <span className="shi-live" style={{ width: 7, height: 7, borderRadius: '50%', background: C.lime }} />
                    Connected
                  </span>
                </div>
                <button
                  onClick={() => navigate('/leads')}
                  className="shi-btn-glass"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4.6px',
                    padding: '5.5px 12px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: '7.4px',
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <ArrowLeft size={12} /> Back to Leads
                </button>
              </div>

              {/* Title + flow visual */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <h1 style={{ margin: 0, fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.4px', lineHeight: 1.2 }}>
                    Google Sheets Integration
                  </h1>
                  <p style={{ margin: '7.4px 0 0', fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, maxWidth: '520px' }}>
                    Pull leads straight from any Google Sheet. Columns are mapped automatically, every row is validated,
                    and nothing reaches your pipeline until you approve it.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                    {FEATURES.map((f) => (
                      <span
                        key={f}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4.6px',
                          padding: '3.7px 9.2px',
                          borderRadius: '20px',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.13)',
                          fontSize: '0.64rem',
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.9)',
                        }}
                      >
                        <Check size={11} color={C.lime} strokeWidth={3} /> {f}
                      </span>
                    ))}
                  </div>
                </div>

                <div
                  className={syncing ? 'shi-flow-run' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 18px',
                    borderRadius: '14px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  {flowNode(<SheetTile size={54} />, 'Google Sheets')}
                  <div style={{ position: 'relative', width: '96px', height: '22px', marginBottom: '18px' }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '50%',
                        borderTop: '2px dashed rgba(255,255,255,0.25)',
                      }}
                    />
                    <span className="shi-flow-dot a" />
                    <span className="shi-flow-dot b" />
                    <span className="shi-flow-dot c" />
                    <span className="shi-flow-dot d" />
                    <div
                      style={{
                        position: 'absolute',
                        top: '-12px',
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: '0.54rem',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: syncing ? C.lime : 'rgba(255,255,255,0.5)',
                      }}
                    >
                      {syncing ? 'Syncing' : 'Ready'}
                    </div>
                  </div>
                  {flowNode(<LeadsTile size={54} />, 'Pitchout Leads')}
                </div>
              </div>

              {/* Segmented control */}
              {segmented}

              {/* Sync bar */}
              <div style={{ display: tab === 'sync' ? 'flex' : 'none', gap: '7.4px', flexWrap: 'wrap' }}>
                <div
                  className="shi-url"
                  style={{
                    flex: '1 1 300px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8.3px',
                    height: '44px',
                    padding: '0 12px',
                    background: importing ? 'rgba(255,255,255,0.85)' : 'white',
                    border: `1px solid ${importError ? 'rgba(248,113,113,0.9)' : 'transparent'}`,
                    borderRadius: '10.1px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                  }}
                >
                  <SheetGlyph size={16} color="#16a34a" />
                  <input
                    value={sheetUrl}
                    disabled={importing}
                    onChange={(e) => {
                      setSheetUrl(e.target.value);
                      if (importError) setImportError(null);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && !importing && handleImport()}
                    placeholder="Paste Google Sheet link — https://docs.google.com/spreadsheets/d/…"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      color: '#111827',
                      fontSize: '0.8rem',
                    }}
                  />
                  {sheetUrl && !importing ? (
                    <button
                      onClick={() => setSheetUrl('')}
                      title="Clear"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.muted, display: 'flex' }}
                    >
                      <X size={13} />
                    </button>
                  ) : (
                    <Link2 size={14} style={{ color: C.muted, flexShrink: 0 }} />
                  )}
                </div>

                {/* Optional readable name — label only, shared by every sync of this sheet */}
                <div
                  className="shi-url"
                  style={{
                    flex: '0 1 210px',
                    minWidth: '160px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    height: '44px',
                    padding: '0 12px',
                    background: importing ? 'rgba(255,255,255,0.85)' : 'white',
                    border: '1px solid transparent',
                    borderRadius: '10.1px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                  }}
                >
                  <Tag size={14} style={{ color: C.muted, flexShrink: 0 }} />
                  <input
                    value={displayName}
                    disabled={importing}
                    maxLength={80}
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !importing && handleImport()}
                    placeholder="Name (optional)"
                    aria-label="Sheet name (optional)"
                    title="A readable name for this sheet, e.g. Diwali Meta Leads. You can rename it anytime."
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      color: '#111827',
                      fontSize: '0.8rem',
                    }}
                  />
                </div>

                <button
                  onClick={handleImport}
                  disabled={importing}
                  className={`shi-sync-btn${importing ? '' : ' shi-btn-lime'}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6.5px',
                    height: '44px',
                    padding: '0 22px',
                    background: C.lime,
                    border: 'none',
                    borderRadius: '10.1px',
                    color: C.teal,
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    letterSpacing: '0.3px',
                    cursor: importing ? 'wait' : 'pointer',
                    opacity: importing ? 0.85 : 1,
                    boxShadow: '0 6px 20px rgba(200,233,75,0.35)',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <RefreshCw size={14} className={importing ? 'shi-spinner' : undefined} />
                  {importing ? 'SYNCING…' : 'SYNC SHEET'}
                </button>
              </div>

              {/* Live sync steps */}
              {importing && tab === 'sync' && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '-4px' }}>
                  {SYNC_STEPS.map((s, i) => {
                    const done = i < syncStep;
                    const active = i === syncStep;
                    return (
                      <div
                        key={s}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10.1px',
                          borderRadius: '20px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          transition: 'all 0.3s',
                          background: active ? 'rgba(200,233,75,0.16)' : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${active ? 'rgba(200,233,75,0.5)' : 'rgba(255,255,255,0.12)'}`,
                          color: done || active ? '#fff' : 'rgba(255,255,255,0.45)',
                        }}
                      >
                        {done ? (
                          <CircleCheck size={12} color={C.lime} />
                        ) : active ? (
                          <span
                            className="shi-spinner"
                            style={{
                              width: '10px',
                              height: '10px',
                              border: '2px solid rgba(200,233,75,0.3)',
                              borderTopColor: C.lime,
                              borderRadius: '50%',
                              display: 'inline-block',
                            }}
                          />
                        ) : (
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                        )}
                        {s}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Sync error ──────────────────────────────────────────────── */}
        {tab === 'sync' && importError && (
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '4.6px' }}>
              <Banner
                tone={TONES.red}
                icon={<TriangleAlert size={15} />}
                actions={
                  <button
                    onClick={() => setImportError(null)}
                    title="Dismiss"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', display: 'flex' }}
                  >
                    <X size={13} />
                  </button>
                }
              >
                {importError}
              </Banner>
            </div>
          </div>
        )}

        {/* ── Last sync result (auto-hides after 10s) ─────────────────── */}
        {tab === 'sync' && lastResult && (
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '14px 16.6px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9.2px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px' }}>
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      background: 'rgba(22,163,74,0.1)',
                      border: '1.5px solid rgba(22,163,74,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={17} color="#16a34a" strokeWidth={3} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>Sheet synced successfully</div>
                    <div style={{ fontSize: '0.68rem', color: C.sub }}>
                      Data quality {qualityPct(lastResult.imported_count, lastResult.failed_count)}% · staged for review
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setLastResult(null)}
                  className="shi-btn-ghost"
                  title="Dismiss"
                  style={{
                    display: 'inline-flex',
                    padding: '6px',
                    background: G.shellBg,
                    border: `1px solid ${G.line}`,
                    borderRadius: '7.4px',
                    color: C.sub,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <X size={12} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
                <StatTile label="Rows Read" value={lastResult.total_rows} />
                <StatTile label="Ready" value={lastResult.imported_count} color="#15803d" />
                <StatTile label="Invalid" value={lastResult.failed_count} color="#c62828" />
              </div>
              {lastResult.total_rows > 0 && (
                <QualityBar valid={lastResult.imported_count} invalid={lastResult.failed_count} showLabels={false} />
              )}

              <Banner
                tone={lastResult.imported_count > 0 ? TONES.lime : TONES.amber}
                icon={lastResult.imported_count > 0 ? <ShieldCheck size={15} /> : <TriangleAlert size={15} />}
                actions={
                  <button
                    onClick={() => setDetailId(lastResult.import_id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4.6px',
                      padding: '7px 14px',
                      background: 'rgb(17,85,80)',
                      border: 'none',
                      borderRadius: '7.4px',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(17,85,80,0.25)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lastResult.imported_count > 0 ? 'REVIEW & PUSH' : 'VIEW ROWS'} <ArrowRight size={12} />
                  </button>
                }
              >
                {lastResult.imported_count > 0
                  ? `${lastResult.imported_count} row${lastResult.imported_count > 1 ? 's are' : ' is'} ready. Nothing is in your leads yet — review and push when you're happy.`
                  : 'No rows passed validation. Check the invalid rows, fix the sheet and sync again.'}
              </Banner>
            </div>
          </div>
        )}

        {/* ── Documentation tab ───────────────────────────────────────── */}
        {tab === 'docs' && <SheetImportsDocs />}

        {tab === 'sync' && (
          <>
        {/* ── Sync history ────────────────────────────────────────────── */}
        {listError && <Message severity="error" text={listError} style={{ width: '100%', fontSize: '0.79rem' }} />}

        <div style={SHELL}>
          <div style={{ ...INNER, overflow: 'hidden' }}>
            <div
              style={{
                padding: '11px 16.6px',
                borderBottom: `1px solid ${G.soft}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '9.2px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
                {sectionTitle('Sync History')}
                <span
                  style={{
                    padding: '1.8px 8.3px',
                    borderRadius: '20px',
                    background: 'rgba(17,85,80,0.07)',
                    border: '1px solid rgba(17,85,80,0.18)',
                    color: C.tealMid,
                    fontSize: '0.63rem',
                    fontWeight: 600,
                  }}
                >
                  {pagination.total} syncs
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px' }}>
                {canSpend && (
                  <button
                    onClick={() => navigate('/leads/spend-report')}
                    className="shi-btn-ghost"
                    style={{
                      height: '31px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '0 10px',
                      background: G.shellBg,
                      border: `1px solid ${G.line}`,
                      borderRadius: '7.4px',
                      color: C.teal,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <IndianRupee size={12} /> Spend Report
                  </button>
                )}
                <select
                  className="shi-sel"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    height: '31px',
                    minWidth: '130px',
                    background: 'white',
                    border: `1px solid ${G.line}`,
                    borderRadius: '7.4px',
                    fontSize: '0.7rem',
                    padding: '0 24px 0 9px',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: SELECT_ARROW,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    color: statusFilter ? C.teal : '#374151',
                  }}
                >
                  <option value="">All Status</option>
                  <option value="completed">Synced</option>
                  <option value="failed">Failed</option>
                  <option value="processing">Syncing</option>
                  <option value="pending">Queued</option>
                </select>
                <button
                  onClick={fetchImports}
                  disabled={loading}
                  className="shi-btn-ghost"
                  title="Refresh"
                  style={{
                    height: '31px',
                    width: '31px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: G.shellBg,
                    border: `1px solid ${G.line}`,
                    borderRadius: '7.4px',
                    color: C.sub,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <RefreshCw size={12} className={loading ? 'shi-spinner' : undefined} />
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={imports}
                dataKey="id"
                loading={loading}
                emptyMessage={emptyState}
                className="shi-dt"
                style={{ fontSize: '0.76rem' }}
              >
                <Column header="Sheet" body={sheetTpl} headerStyle={{ ...CH, minWidth: '190px' }} />
                <Column header="Status" body={(r: SheetImport) => <StatusPill status={r.status} />} headerStyle={CH} />
                <Column header="Data Quality" body={qualityTpl} headerStyle={CH} />
                <Column header="Leads" body={pushTpl} headerStyle={CH} />
                {canSpend && <Column header="Spend" body={spendTpl} headerStyle={CH} />}
                <Column header="Synced By" body={byTpl} headerStyle={CH} />
                <Column
                  header="Actions"
                  body={actionTpl}
                  headerStyle={{ ...CH, textAlign: 'center' }}
                  bodyStyle={{ textAlign: 'center' }}
                />
              </DataTable>
            </div>

            <CustomPaginator
              page={page}
              totalPages={pagination.pages}
              totalRecords={pagination.total}
              pageSize={limit}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 50]}
              showInfoBar
            />
          </div>
        </div>
          </>
        )}
      </div>

      {/* ── Re-sync (confirm → progress → result) ─────────────────────── */}
      <ResyncModal
        visible={!!resyncTarget}
        phase={resyncPhase}
        target={resyncTarget ? sheetLabel(resyncTarget) : undefined}
        result={resyncResult}
        error={resyncError}
        onCancel={closeResync}
        onConfirm={runResync}
        onReview={(id) => {
          setResyncTarget(null);
          setDetailId(id);
        }}
      />

      {/* ── Delete confirm ────────────────────────────────────────────── */}
      <ConfirmDeleteModal
        visible={!!deleteTarget}
        loading={!!deletingId}
        target={deleteTarget ? sheetLabel(deleteTarget) : undefined}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {/* ── Rename sheet (all its syncs + beacon) ─────────────────────── */}
      <SheetRenameModal
        visible={!!renameTarget}
        sheetId={renameTarget ? sheetIdOf(renameTarget) : null}
        currentName={renameTarget?.display_name}
        onHide={() => setRenameTarget(null)}
        onRenamed={(sheetId, name) => {
          // update every row of this sheet in place — no refetch needed
          setImports((list) => list.map((x) => (sheetIdOf(x) === sheetId ? { ...x, display_name: name } : x)));
          toast.current?.show({ severity: 'success', summary: name ? 'Sheet renamed' : 'Name cleared', detail: name || shortId(sheetId), life: 2500 });
        }}
      />

      {/* ── Details / push modal ──────────────────────────────────────── */}
      <SheetImportDetailsModal
        visible={!!detailId}
        importId={detailId}
        onHide={() => setDetailId(null)}
        onPushed={fetchImports}
        onSpendChanged={(sp) => setSpends((m) => ({ ...m, [sp.sheet_import_id]: sp }))}
      />
    </>
  );
};

export default SheetImports;